import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonComponent } from '../../component/layout/element/button.component';
import { ListingPublic } from '../../../../globalTypes/listings.types';
import { Store } from '@ngrx/store';
import { ListingService } from '../../services/listing.service';
import { Base } from '../../component/wrapper/base-wrapper.component';
import { lastValueFrom, take, takeUntil } from 'rxjs';
import { listingsFeature, ListingsPageActions } from '../../store/listings.store';
import { ActivatedRoute, Router } from '@angular/router';
import { FormInputNumberComponent } from "../../component/forms/form-input-number.component";
import { MatSnackBar } from '@angular/material/snack-bar';
import { shipmentDistanceUnitList, shipmentWeightUnitList, ShippingDistanceUnit, ShippingInfo, ShippingType, ShippingTypeInfo, ShippingWeightUnit } from '../../../../globalTypes/fulfillment.types';

import { MatMenuModule } from '@angular/material/menu';
import { getCategoryShipmentPreset } from '@dataconnect/account';
import { ParcelDimensionsComponent } from "../../component/shipping/parcel-dimensions.component";
import { ParcelUnitsComponent } from "../../component/shipping/parcel-units.component";

@Component({
  selector: 'msh-delivery',
  imports: [ButtonComponent, MatMenuModule, FormsModule, FormInputNumberComponent, ParcelDimensionsComponent, ParcelUnitsComponent],
  template: `
  @if(listing){ 
    @if(listing.category?.isDigital){
      <div class="p-4 max-w-96 m-auto">
        <div class="mb-4 text-lg ">Digital Material</div>
        When users purchase this digital product, they will be able to download it from their <span class=" text-orange-500">orders -> files</span> page.
        <!-- maybe show which files are available for which variation... maybe once I have build the resources page I could leverage it -->
      </div>
    }@else {
      <div class="flex items-center justify-center gap-0 mb-4">
        <div (click)="changeShippingType('Standard')" [class]="(shipmentType !== 'Free Shipping' && shipmentType !== 'Fixed Price' ? 'bg-copy/25 ' : 'text-copy/75')+' cursor-pointer hover:bg-copy/15 border border-copy/50 rounded-l-lg border-r-0 text-copy px-3 py-1 '">Standard Shipping</div>
        <div (click)="changeShippingType('Fixed Price')" [class]="(shipmentType === 'Fixed Price' ? 'bg-copy/25 ' : 'text-copy/75')+' cursor-pointer hover:bg-copy/15 border border-copy/50 px-3 py-1 text-copy'">Fixed Price</div>
        <div (click)="changeShippingType('Free Shipping')" [class]="(shipmentType === 'Free Shipping' ? 'bg-copy/25 ' : 'text-copy/75')+' cursor-pointer hover:bg-copy/15 border border-copy/50 rounded-r-lg px-3 py-1 text-copy'">Free Shipping</div>
      </div>
      @if(shipmentType){
        <div class=" flex flex-wrap mt-2 mb-10">
          <div class="flex justify-end w-full items-center text-copy/50 text-xs min-w-11">
            @if( shipmentType !== "Free Shipping" && shipmentType !== "Fixed Price"){
              
              <msh-parcel-units (parcelUpdated)="evaluateRequirementsMeet()" [(parcel)]="listing"></msh-parcel-units>
            }
          </div>
        </div>
      }
      @switch(shipmentType){
        @case ("Free Shipping") {
          <div class="null text-center mb-24">No shipping cost will be charged to the buyer.</div>
        }
        @case ("Fixed Price") {
          <div class="flex flex-wrap justify-center gap-4 mb-7">
            <msh-form-input-number
              class=" max-w-52"
              [price]="listing.shippingCost"
              [placeholder]="'Fixed shipping price'"
              [limiterValue]="2000"
              (onImmediatePriceChange)="shippingCostChange($event)"
            ></msh-form-input-number>
            @if(multiplesAvailable){
              <div class="flex flex-col justify-center gap-1">
                <msh-form-input-number
                  class=" max-w-52"
                  [price]="listing.shippingAdditionalCost"
                  [placeholder]="'Price per additional item'"
                  [limiterValue]="2000"
                  (onImmediatePriceChange)="shippingAdditionalCostChange($event)"
                ></msh-form-input-number>
                <div class="relative -top-5 text-sm italic opacity-50">(leave blank if non-applicable)</div>
              </div>
            } @else {
              <div class="null text-center mb-7">Price for additional items not required.</div>
            }
          </div>
        }
        @default {
          <msh-parcel-dimensions 
            [(parcel)]="listing" 
            [shipmentType]="shipmentType" 
            (parcelUpdated)="evaluateRequirementsMeet()"
            class="flex flex-col gap-4"
          ></msh-parcel-dimensions>
          <div class="text-center max-w-96 m-auto mb-7">A shipping label will be created at checkout based on the quantity and dimensions&nbsp;provided.</div>
        }
      }
      @if(shipmentType === "Free Shipping" || shipmentType === "Fixed Price"){
        <div class="text-center max-w-96 m-auto mb-7">It will be your responsibility to deliver the item to the&nbsp;buyer.</div>
      }
    }
  }
  <div class="grow"></div>
  <msh-button class="mb-14" (onClick)="onSubmitClick($event)" 
    [disabled]="!shippingRequirementsMeet"
    [isLoading]="isLoading" [cta]="cta">
  </msh-button>

  `,
  styles: `:host { display: flex; flex-direction: column; flex-grow: 1; height: 100%;}`
})
export class DeliveryComponent extends Base<DeliveryComponent> {
  listing: ListingPublic | Partial<ListingPublic> | null = null;
  isLoading = false;
  multiplesAvailable = false;
  shipmentWeightUnitList: ShippingWeightUnit[] = shipmentWeightUnitList
  shipmentDistanceUnitList: ShippingDistanceUnit[] = shipmentDistanceUnitList;
  shipmentType?: ShippingType = "Standard"
  preset?: ShippingInfo;
  presetApplied = false;
  shippingRequirementsMeet = false;
  cta = 'Update';

  constructor(
    private router: Router,
    private store: Store,
    private snackbar: MatSnackBar,
    private listingService: ListingService,
  ){ super(); 
  }

  async ngOnInit(): Promise<void> {
    this.store.select(listingsFeature.selectListing).pipe(takeUntil(this.$destroy)).subscribe(async listing=>{
      console.log({listing})
      if(!listing) return; 
      this.listing = JSON.parse(JSON.stringify(listing));
      if(!this.listing || !this.listing?.id || this.listing.id === 'new') {
        this.cta = 'Next';
      }

      console.log({place: 'onInit ', listing: this.listing, shippingType: this.listing?.shippingType})
      if(this.listing?.categoryId){
        console.log({category: this.listing.categoryId})
        if(!this.listing.shippingType || this.listing.shippingType.length === 0){
          if(this.listing?.category?.isDigital) {
            this.listing.shippingType = this.shipmentType = "Digital Download";
            this.evaluateRequirementsMeet();
          }else{
            const categoryRef = await getCategoryShipmentPreset({categoryId: this.listing.categoryId});
            const preset = categoryRef.data.category?.shippingInfoPreset as ShippingInfo || false
            if(preset){
              this.preset = preset
              this.setPresetDimensions();
            }
          }
        } else {
          this.shipmentType = this.listing.shippingType;
        }
      }
      this.multiplesAvailable = !!this.listing && !!this.listing.listingVariations_on_listing
        && this.listing.listingVariations_on_listing.some(v => v.quantity && v.quantity > 1)
      if(!this.listing?.id || this.listing.id === 'new'){
        this.evaluateRequirementsMeet();
      }

    })
  }

  changeShippingType(type: ShippingType){
    this.shipmentType = type;
    this.shippingRequirementsMeet = false;
    if(!this.listing) return;
    // await add presets
    this.listing.shippingType = type;
    this.setPresetDimensions();
    this.evaluateRequirementsMeet();
  }

  shippingCostChange(ev: any){
    if(this.listing) this.listing.shippingCost = ev;
    this.evaluateRequirementsMeet()
  }

  shippingAdditionalCostChange(ev: any){
    if(this.listing) this.listing.shippingAdditionalCost = ev;
    this.evaluateRequirementsMeet()
  }
  evaluateRequirementsMeet(){
    console.log({place: 'evaluateRequirementsMeet', shipmentType: this.shipmentType, listing: this.listing, multiplesAvailable: this.multiplesAvailable})
    switch(this.shipmentType){
      case 'Digital Download':
      case 'Free Shipping':
        this.shippingRequirementsMeet = true;
        break;
      case 'Fixed Price':
        this.shippingRequirementsMeet = this.listing?.shippingCost && (!this.multiplesAvailable || this.listing.shippingAdditionalCost !== 0) || false
        break;
      default:
        this.shippingRequirementsMeet = !!this.listing && !!this.listing.weight && !!this.listing.height && !!this.listing.width && !!this.listing.length
    }
    if(this.listing && this.listing.category?.isDigital){
      this.shippingRequirementsMeet = true;
      return;
    }
  }

  setPresetDimensions(){
    if(!this.listing || !this.preset || this.presetApplied) return;
    const {height, width, length, weight, compressionRate, distanceUnit, weightUnit} = this.preset;
    this.listing.height = height
    this.listing.width = width
    this.listing.length = length
    this.listing.weight = weight
    this.listing.distanceUnit = distanceUnit
    this.listing.weightUnit = weightUnit
    this.listing.shippingAdditionalCost = compressionRate
    this.presetApplied = true;
  }

  cleanShippingSelections(){
    if(this.listing && this.shipmentType !== 'Fixed Price'){
      this.listing.shippingCost = this.shipmentType === 'Free Shipping' ? 0 : undefined;
      this.listing.shippingAdditionalCost = undefined;
    }
  }

  async onSubmitClick(ev: any){
    if(!this.listing) return;
    this.isLoading = true; 
    this.shippingRequirementsMeet = false;
    console.log({listing: this.listing})
    let nextPage =  undefined;
    this.cleanShippingSelections();
    if(this.listing.id === "new"){
      nextPage = 'status';
      this.store.dispatch(ListingsPageActions.progressListing({listing: this.listing as Partial<ListingPublic>}))
    } else {
      this.isLoading = true;
      await this.listingService.saveListing(this.listing);
      this.snackbar.open("Listing price & stock have been saved", "close", {duration: 8000})
      this.isLoading = false;
    }
    if(!this.listing.name) nextPage = 'details';
    if(!this.listing.lowestPrice) nextPage = 'price-stock';
    if(nextPage) this.router.navigate(['manage-listing', nextPage, this.listing.id])

  }
}
